import { Suspense, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useSuspenseQuery } from "@tanstack/react-query";

import {
  getPayments,
  getPlans,
} from "~/data/subscription/subscription.service";

import { Checkout } from "./checkout";
import { Button } from "../shared/button";
import { Dialog } from "../shared/dialog";
import { useUser } from "../shared/queries/user-query";
// import { useUser } from "../shared/queries/user-query";

export function Payments() {
  const user = useUser();
  console.log(user);
  return (
    <div className="px-10 py-5 bg-[#FBFBFB] rounded-[10px]">
      <header className="flex items-center justify-between mb-5">
        <h2 className="text-3xl font-alternative font-semibold leading-10">
          Assine um dos Planos
        </h2>
        <p>
          Escolha um dos planos para utilizar a plataforma.
          <br />{" "}
          {user.is_trial ? (
            user.trial_end_at && (
              <span>
                Sua assinatura gratuita encerra em{" "}
                <span className="text-red-500">
                  {new Date(user.trial_end_at).toLocaleDateString("pt-BR")}
                </span>
                .
              </span>
            )
          ) : user.user_subscription ? (
            <span>
              Sua assinatura será cobrada em{" "}
              <span className="text-red-500">
                {new Date(user.user_subscription.valid_date).toLocaleDateString(
                  "pt-BR"
                )}
              </span>
              .
            </span>
          ) : (
            user.trial_end_at && (
              <span>
                Sua assinatura gratuita encerrou em{" "}
                <span className="text-red-500">
                  {new Date(user.trial_end_at).toLocaleDateString("pt-BR")}
                </span>
                .
              </span>
            )
          )}
        </p>
      </header>

      <section className="mb-5">
        <Suspense>
          <PlansCards />
        </Suspense>
      </section>

      <section>
        <h3 className="text-lg font-medium leading-6 font-alternative text-[#5B5B5B] py-4 px-[10px]">
          Pagamentos
        </h3>

        <Suspense>
          <ErrorBoundary
            fallbackRender={(props) => <div>Error: {props.error.message}</div>}
          >
            <PaymentsTable />
          </ErrorBoundary>
        </Suspense>
      </section>
    </div>
  );
}

function PlansCards() {
  const { data: plans } = useSuspenseQuery({
    queryKey: ["plans"],
    queryFn: () => getPlans(),
    staleTime: Infinity,
    select: (data) => [...data].sort((a, b) => a.value - b.value),
  });
  const user = useUser();

  const [planId, setPlanId] = useState("");
  const plan = plans.find((it) => it.id === planId);
  const currentSubscription = user.user_subscription;

  return (
    <div className="grid lg:grid-cols-3 gap-5 items-center rounded-3xl px-5 py-10 bg-white shadow-card">
      {plans.map((plan, i) => {
        const color = ["#128B91", "#1AAF50", "#FA4F3B"][i];
        return (
          <article
            key={plan.id}
            className="flex flex-col bg-[#F4F4F4] rounded-[10px] h-full"
          >
            <header className="px-5 py-2">
              <h3 className="text-3xl text-center font-semibold font-alternative">
                {plan.name}
              </h3>
              <p
                className="text-xl text-center font-alternative font-semibold"
                style={{ color }}
              >
                {plan.value.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  maximumFractionDigits: 2,
                })}
              </p>
            </header>
            <p className="px-6 py-2 text-center">{plan.description}</p>
            <footer className="p-3 mt-auto">
              {currentSubscription ? (
                <Button
                  variant="default"
                  className="w-full h-10"
                  style={{ color: color, borderColor: color }}
                  type="button"
                  onClick={() => {
                    if (
                      currentSubscription.billing_type !== "pix" ||
                      currentSubscription.status !== "waiting_payment"
                    )
                      return;
                    setPlanId(plan.id);
                  }}
                >
                  {renderSubscriptionStatus(
                    currentSubscription.status,
                    currentSubscription.billing_type
                  )}
                </Button>
              ) : (
                <Button
                  variant="default"
                  className="w-full h-10"
                  style={{ color: color, borderColor: color }}
                  type="button"
                  onClick={() => setPlanId(plan.id)}
                >
                  Assinar
                </Button>
              )}
            </footer>
          </article>
        );
      })}

      <Dialog open={!!plan} onOpenChange={() => setPlanId("")}>
        {!!plan && (
          <Dialog.Content
            style={{ width: 800 }}
            className="max-w-full"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <Dialog.Title className="sr-only">Checkout</Dialog.Title>
            <Dialog.Description className="sr-only">
              Pague com Cartão ou Pix
            </Dialog.Description>
            <Checkout product={plan} initialState={currentSubscription} />
          </Dialog.Content>
        )}
      </Dialog>
    </div>
  );
}

function PaymentsTable() {
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const { data: paginator } = useSuspenseQuery({
    queryKey: ["payments", page],
    queryFn: () => getPayments(page),
  });

  if (!paginator.data.length) {
    return <p className="p-3">Nenhum pagamento ainda</p>;
  }

  return (
    <table className="w-full border-separate border-spacing-0">
      <thead>
        <tr className="bg-[#D3D3D3]">
          <th className="max-w-0 w-3/4 py-2 px-5 uppercase text-sm text-start leading-4 font-alternative rounded-tl-[10px] font-normal border-b border-b-[#5B5B5B]">
            Assinatura
          </th>
          <th className="uppercase text-sm text-center leading-4 font-alternative font-normal border-b border-b-[#5B5B5B]">
            Data
          </th>
          <th className=" py-2 px-5 uppercase text-sm text-center leading-4 font-alternative rounded-tr-[10px] font-normal border-b border-b-[#5B5B5B]">
            Valor
          </th>
        </tr>
      </thead>
      <tbody className="bg-[#F4F4F4] rounded-b-[10px]">
        {paginator.data.map((dto) => (
          <tr key={dto.id}>
            <td className="text-sm text-start py-2 px-5">
              {dto.subscription?.name ?? "-"}
            </td>
            <td className="text-sm text-center">
              {new Date(dto.due_date || dto.created_at).toLocaleDateString()}
            </td>
            <td className="text-sm text-center text-primary-1 py-2 px-5">
              {dto.value.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderSubscriptionStatus(status: string, type: string) {
  switch (status) {
    case "waiting_payment":
      return type === "pix" ? "Concluir pagamento" : "Aguardando pagamento";
    case "confirmed":
      return "Ativo";
    case "paid":
      return "Pago";
    case "created":
      return "Criado";
    default:
      return status;
  }
}
